import React from 'react';
import { useTranslation } from 'react-i18next';

interface LocalizedDateProps {
  date: Date | string | number;
  format?: 'short' | 'medium' | 'long' | 'full';
  includeTime?: boolean;
}

export const LocalizedDate: React.FC<LocalizedDateProps> = ({
  date,
  format = 'medium',
  includeTime = false,
}) => {
  const { i18n } = useTranslation();
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;

  const presets = {
    short: { month: 'numeric' as const, day: 'numeric' as const, year: '2-digit' as const },
    medium: { month: 'short' as const, day: 'numeric' as const, year: 'numeric' as const },
    long: { month: 'long' as const, day: 'numeric' as const, year: 'numeric' as const },
    full: { weekday: 'long' as const, month: 'long' as const, day: 'numeric' as const, year: 'numeric' as const },
  } as const;

  let formatOptions: Intl.DateTimeFormatOptions = { ...presets[format] };

  if (includeTime) {
    formatOptions.hour = 'numeric';
    formatOptions.minute = '2-digit';
  }

  const formattedDate = new Intl.DateTimeFormat(i18n.language, formatOptions).format(dateObj);

  return <time dateTime={dateObj.toISOString()}>{formattedDate}</time>;
};

interface LocalizedCurrencyProps {
  amount: number;
  currency?: string;
  showSymbol?: boolean;
}

export const LocalizedCurrency: React.FC<LocalizedCurrencyProps> = ({
  amount,
  currency = 'USD',
  showSymbol = true,
}) => {
  const { i18n } = useTranslation();

  const formatted = new Intl.NumberFormat(i18n.language, {
    style: showSymbol ? 'currency' : 'decimal',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);

  return <span>{formatted}</span>;
};

interface LocalizedNumberProps {
  value: number;
  notation?: 'standard' | 'compact';
  maximumFractionDigits?: number;
}

export const LocalizedNumber: React.FC<LocalizedNumberProps> = ({
  value,
  notation = 'standard',
  maximumFractionDigits = 0,
}) => {
  const { i18n } = useTranslation();

  const formatted = new Intl.NumberFormat(i18n.language, {
    notation,
    maximumFractionDigits,
  }).format(value);

  return <span>{formatted}</span>;
};

interface LocalizedRelativeTimeProps {
  date: Date | string | number;
}

export const LocalizedRelativeTime: React.FC<LocalizedRelativeTimeProps> = ({ date }) => {
  const { t, i18n } = useTranslation();
  const dateObj = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - dateObj.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);
  const diffYears = Math.floor(diffDays / 365);

  let relativeTime: string;

  if (diffSeconds < 60) {
    relativeTime = t('time.justNow');
  } else if (diffMinutes < 60) {
    relativeTime = t('time.minuteAgo', { count: diffMinutes });
  } else if (diffHours < 24) {
    relativeTime = t('time.hourAgo', { count: diffHours });
  } else if (diffDays < 7) {
    relativeTime = t('time.dayAgo', { count: diffDays });
  } else if (diffWeeks < 4) {
    relativeTime = t('time.weekAgo', { count: diffWeeks });
  } else if (diffMonths < 12) {
    relativeTime = t('time.monthAgo', { count: diffMonths });
  } else {
    relativeTime = t('time.yearAgo', { count: diffYears });
  }

  return (
    <time dateTime={dateObj.toISOString()} title={dateObj.toLocaleString(i18n.language)}>
      {relativeTime}
    </time>
  );
};

interface LocalizedListProps {
  items: string[];
  type?: 'conjunction' | 'disjunction';
}

export const LocalizedList: React.FC<LocalizedListProps> = ({ items, type = 'conjunction' }) => {
  const { i18n } = useTranslation();

  const formatted = new Intl.ListFormat(i18n.language, {
    style: 'long',
    type,
  }).format(items);

  return <span>{formatted}</span>;
};
